import { Size, printTitleCase } from '@bms2/shared';
import { sizeClassMap } from '../classMaps';
import styles from './Pill.module.css';

/**
 * Base Pill (piece 3): foreground, background, text and size only, with no
 * status awareness and no click handler. The height per Size and the font
 * size, floored at 11px for Small, are the tokens of piece 1.
 *
 * 🔒 Every pill reads in Title Case, whatever its text's source (T427;
 * decision 1110). The text is passed through the shared `printTitleCase`
 * function decision 1060 built for the printed sheet, so a status enum's
 * seeded label, a fixed string and a label a page computes all reach the
 * screen the same way, without any of them needing to be reworded for the
 * pill alone.
 *
 * 🔨 Text a person coined, or a record's own name, is drawn as typed
 * (`asTyped`; decision 1110, amended 2026-09-28): a tag's label, the record a
 * message is tagged to, a department's name. Title-casing it would put a
 * spelling on the screen that nobody wrote.
 */
export interface PillProps {
  text: string;
  /** A colour token expression such as `var(--status-good-text)`; never a hex. */
  foreground: string;
  background: string;
  size?: Size;
  /**
   * Draws the pill's edge in its own foreground (piece 1). A tinted fill reads
   * between 1.1:1 and 1.3:1 against every surface a pill sits on, so the fill
   * cannot say where the pill ends — an uncoloured tag chip "blended" into the
   * page for exactly that reason. The edge is what gives it a shape.
   */
  outlined?: boolean;
  /**
   * Draws the text exactly as it was typed, skipping Title Case (decision 1110).
   * Only for text a person coined or a record's own name, never a status label.
   */
  asTyped?: boolean;
  /**
   * For a pill set in a narrow box (a visit block on the Schedule): the pill never grows past the box, and a label that does
   * not fit ends in an ellipsis. The pill takes no pointer, so the box around it carries the full words on hover and for a
   * screen reader (piece 29; T3873).
   */
  truncate?: boolean;
}

export const pillDocumentedStates = ['small', 'medium', 'large', 'outlined'] as const;

const sizeClass = sizeClassMap({ small: styles.small!, medium: styles.medium!, large: styles.large! });

export function Pill({ text, foreground, background, size = Size.Medium, outlined = false, asTyped = false, truncate = false }: PillProps) {
  return (
    <span
      className={`${styles.pill} ${sizeClass[size]}${outlined ? ` ${styles.outlined}` : ''}${truncate ? ` ${styles.truncate}` : ''}`}
      style={{ color: foreground, background }}
      data-size={size}
      {...(outlined ? { 'data-pill-outlined': 'true' } : {})}
      {...(truncate ? { 'data-pill-truncate': 'true' } : {})}
    >
      {truncate ? <span className={styles.label}>{asTyped ? text : printTitleCase(text)}</span> : asTyped ? text : printTitleCase(text)}
    </span>
  );
}
